import { Cohort, Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { SupportedLocale } from './country'
import { FetchVerificationErrorResponse, FetchVerificationSuccessResponse, FetchVerificationWaitResponse } from '@afterpaytouch/event-verify'
import { CodeVerificationDirective } from './auth'
import {
  VerificationMethod,
  Mobile,
  Email,
  RegistrationId,
  Code,
  RegistrationData,
  RegistrationErrorCode,
  registrationErrors as baseRegistrationErrors,
  VerifyRegistrationSMSAuthorizationResponse,
  VerifyRegistrationSMSAuthorizationCode,
  VerifyRegistrationSMSAuthorizationMessage,
  AddMobileToRegistrationRequest,
  AddEmailToRegistrationRequest,
  ConfirmRegistrationRequest,
  FinaliseAccountRequest,
  CreateRegistration,
  CreateAccountRequest,
} from '@afterpaytouch/registration'

// Extended version of registrationErrors
export const registrationErrors = { ...baseRegistrationErrors }

// Re-export these for backward compatibility
export { RegistrationErrorCode, VerificationMethod, VerifyRegistrationSMSAuthorizationCode, VerifyRegistrationSMSAuthorizationMessage }
export type {
  Mobile,
  Email,
  RegistrationId,
  Code,
  RegistrationData,
  VerifyRegistrationSMSAuthorizationResponse,
  AddMobileToRegistrationRequest,
  AddEmailToRegistrationRequest,
  ConfirmRegistrationRequest,
  FinaliseAccountRequest,
  CreateRegistration,
  CreateAccountRequest,
}

export enum RegistrationStatus {
  NotStarted,
  Created,
  CodeSent,
  CodeVerified,
  Completed,
  IDVerified,
  ContractAgreementAccepted,
  Error,
}

export interface RiskRegistrationStatus {
  emailVerificationRequired: boolean
  mobileVerificationRequired: boolean
  isEmailVerified: boolean
}

export interface CreateRegistrationRequest {
  email: Email
  preferredLocale: SupportedLocale
}

export interface CreateRegistrationResponse {
  registrationId: RegistrationId
  registrationStatus: Omit<RiskRegistrationStatus, 'isEmailVerified'>
}

export interface TransformedCreateRegistrationResponse extends CreateRegistrationResponse {
  email: string
}

export interface CreditCheckConsumerProfile {
  givenNames: string
  otherGivenNames: string
  surname: string
  dateOfBirth: string
  address1: string
  address2?: string
  suburb: string
  state?: string
  postcode: string
  creditCheckAgree?: boolean
}

export interface RegistrationVerificationPayload {
  mobile: Mobile
  preferredLocale: SupportedLocale
}

export interface MobileRegistrationVerificationRequest {
  registrationId: RegistrationId
  mobile: Mobile
  countryCode: Country
  preferredLocale: SupportedLocale
  amplitudeSessionId: number
  transactionToken: string
  consumerProfile: CreditCheckConsumerProfile
}

export interface EmailRegistrationVerificationRequest {
  registrationId: RegistrationId
  email: Email
}

export interface RegistrationVerificationResponse extends CodeVerificationDirective {
  smsEnabled: boolean
}

export const duplicateErrorCodeList: RegistrationErrorCode[] = [
  RegistrationErrorCode.ExistingPhoneNumberSendEmail,
  RegistrationErrorCode.ExistingPhoneNumber,
  RegistrationErrorCode.ExistingPhoneNumberUnicorn,
]

export interface RegistrationError {
  error: RegistrationErrorCode
  errorCode?: ErrorCode
  errorInfo?: {
    cohort: Cohort
  }
}

export type CashUserMissingData = Array<'email' | 'mobile' | 'givenNames' | 'surname' | 'dateOfBirth' | 'address'>

export interface CashUserMissingDataObject {
  address1?: string
  address2?: string
  area2?: string
  dateOfBirth?: string
  givenNames?: string
  mobile?: Mobile
  email?: string
  otherGivenNames?: string
  postcode?: string
  state?: string
  suburb?: string
  surname?: string
  countryCode?: Country
}

export interface ConfirmCashUserRegistrationRequest {
  registrationId: string
  origin: 'CONSUMER_PORTAL' | 'CHECKOUT' | 'MOBILE' | 'UNKNOWN'
  preferredLocale: SupportedLocale
  profile: CashUserMissingDataObject
}

// export interface ConfirmCashUserRegistrationResponse {}

export interface VerifyRegistrationMobileRejectedActionPayload {
  errorCode: ErrorCode
}

export interface VerifyRegistrationMobileEventRequest {
  onSuccess: (d: FetchVerificationSuccessResponse) => Promise<void>
  onError: (d: FetchVerificationErrorResponse) => void
  onWaiting: (d: FetchVerificationWaitResponse) => void
}

export interface VerifyRegistrationAuthorizationRequest {
  registrationId: RegistrationId
  amplitudeSessionId: number
  transactionToken: string
  consumer: RegistrationData
}

// Note: VerifyRegistrationSMSAuthorizationResponse, VerifyRegistrationSMSAuthorizationCode, and VerifyRegistrationSMSAuthorizationMessage are now imported from '@afterpaytouch/registration'
